import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  Header,
  HeaderBrand,
  HeaderDivider,
  HeaderMenu,
  HeaderMenuLeft,
  HeaderMenuRight,
  Typography,
  Dropdown,
  DropdownTrigger,
  DropdownOverlay,
  Button,
  MenuItemList,
  MenuItem,
  MenuItemLabel,
  Icon,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ModalBody,
  ModalDivider,
  ModalFooter
} from '@qtm/react2';
import { useTheme } from '../ThemeContext';
import { AuthContext } from '../AuthContext';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import RolesDisplay, { getRoles, hasAdminRole } from "./RolesDisplay";
import {
  SELECTED_SUBSCRIPTION_STORAGE_KEY,
  LEGACY_SELECTED_SUBSCRIPTION_STORAGE_KEY,
  SUBSCRIPTION_ACCOUNT_ID_STORAGE_KEY,
  SUBSCRIPTION_SELECTED_EVENT,
  extractSubscriptionAccountId,
} from '../constants/subscriptions';
import useMediaQuery from '../hooks/useMediaQuery';

function ThemeToggle() {
  const { toggleDarkMode } = useTheme();

  return (
    <Button variant="ghost" onClickEvent={toggleDarkMode}>
      <Icon icon="dark_mode"></Icon>
    </Button>
  );
}

const AppHeader = () => {
  const { darkMode } = useTheme();
  const logoSrc = darkMode
    ? 'https://companieslogo.com/img/orig/HO.PA_BIG.D-d4a2a726.svg'
    : 'https://upload.wikimedia.org/wikipedia/commons/4/43/Thales.svg';
  const { instance, accounts } = useMsal();
  const activeAccount = instance.getActiveAccount() ?? accounts?.[0] ?? null;
  const isAuthenticated = useIsAuthenticated();

  const [settingsOpen, setSettingsOpen] = React.useState(false);

  const { getToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [roles, setRoles] = useState([]);       // <-- no generic

  const [resourcesMenuOpen, setResourcesMenuOpen] = useState(false);
  const [dashboardMenuOpen, setDashboardMenuOpen] = useState(false);
  // If your build doesn’t support optional chaining, use:
  // const account = (accounts && accounts.length > 0) ? accounts[0] : null;
  const account = accounts?.[0] || null;
  const [isAdmin, setIsAdmin] = useState(null); // null = unknown, boolean once resolved

  useEffect(() => {
      let cancelled = false;
  
      async function loadRoles() {
        if (!isAuthenticated || !instance || !account) {
          if (!cancelled) {
            setRoles([]);
            setRolesLoaded(true);
          }
          return;
        }
  
        try {
          const r = await getRoles(instance, account);
          if (!cancelled) {
            setRoles(Array.isArray(r) ? r : []);
            setRolesLoaded(true);
          }
        } catch (e) {
          console.error("Failed to load roles:", e);
          if (!cancelled) {
            setRoles([]);
            setRolesLoaded(true);
          }
        }
      }
  
      loadRoles();
      return () => { cancelled = true; };
    }, [isAuthenticated, instance, account]);

  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState('');
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      const migrated = localStorage.getItem(SELECTED_SUBSCRIPTION_STORAGE_KEY);
      if (migrated) {
        return migrated;
      }
      const legacy = localStorage.getItem(LEGACY_SELECTED_SUBSCRIPTION_STORAGE_KEY);
      return legacy || null;
    } catch (err) {
      console.warn('AppHeader: unable to read stored subscription on init', err);
      return null;
    }
  });
  const prevIsAuthenticatedRef = useRef(isAuthenticated);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const deriveAccountIdFromSubscription = useCallback((subscription) => {
    if (!subscription || typeof subscription !== 'object') {
      return extractSubscriptionAccountId(subscription);
    }
    const candidates = [
      subscription?.properties?.displayName,
      subscription?.name,
    ];
    for (const candidate of candidates) {
      const accountId = extractSubscriptionAccountId(candidate);
      if (accountId) {
        return accountId;
      }
    }
    return '';
  }, []);

  const broadcastSubscriptionSelection = useCallback((subscriptionName, accountId) => {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
      return;
    }
    try {
      window.dispatchEvent(new CustomEvent(SUBSCRIPTION_SELECTED_EVENT, {
        detail: {
          subscriptionName: subscriptionName || '',
          accountId: accountId || '',
        },
      }));
    } catch (err) {
      console.warn('AppHeader: unable to broadcast subscription selection', err);
    }
  }, []);

  const persistSubscriptionSelection = useCallback((subscription) => {
    const resolvedName =
      (subscription && typeof subscription === 'object' && subscription?.name)
        ? subscription.name
        : (typeof subscription === 'string' ? subscription : '') || '';

    setSelectedSubscriptionId(resolvedName || null);

    try {
      if (resolvedName) {
        localStorage.setItem(SELECTED_SUBSCRIPTION_STORAGE_KEY, resolvedName);
        localStorage.removeItem(LEGACY_SELECTED_SUBSCRIPTION_STORAGE_KEY);
      } else {
        localStorage.removeItem(SELECTED_SUBSCRIPTION_STORAGE_KEY);
      }
    } catch (err) {
      console.warn('AppHeader: unable to persist subscription selection', err);
    }

    const accountId = deriveAccountIdFromSubscription(subscription);
    try {
      if (accountId) {
        localStorage.setItem(SUBSCRIPTION_ACCOUNT_ID_STORAGE_KEY, accountId);
      } else {
        localStorage.removeItem(SUBSCRIPTION_ACCOUNT_ID_STORAGE_KEY);
      }
    } catch (err) {
      console.warn('AppHeader: unable to persist subscription account id', err);
    }

    broadcastSubscriptionSelection(resolvedName, accountId);
  }, [broadcastSubscriptionSelection, deriveAccountIdFromSubscription]);

  const handleSelectSubscription = (subscriptionName) => {
    if (!subscriptionName) {
      return;
    }
    const match = subscriptions.find((item) => item?.name === subscriptionName);
    persistSubscriptionSelection(match || subscriptionName);
  };

  const ensureIsAdmin = useCallback(async () => {
    if (isAdmin !== null) return isAdmin;
    if (!instance || !activeAccount) {
      setIsAdmin(false);
      return false;
    }
    try {
      const roles = await getRoles(instance, activeAccount);
      const admin = hasAdminRole(roles);
      setIsAdmin(admin);
      return admin;
    } catch (e) {
      console.info('ensureIsAdmin: failed to resolve roles, falling back to non-admin', e);
      setIsAdmin(false);
      return false;
    }
  }, [activeAccount, instance, isAdmin]);

  useEffect(() => {
    if (isAuthenticated) {
      ensureIsAdmin().catch(() => {});
    } else {
      setIsAdmin(null);
    }
  }, [ensureIsAdmin, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSubscriptions([]);
      setSelectedSubscriptionId(null);
      setSubscriptionsError('');
      setSubscriptionsLoading(false);
    }

    if (prevIsAuthenticatedRef.current && !isAuthenticated) {
      try {
        localStorage.removeItem(SELECTED_SUBSCRIPTION_STORAGE_KEY);
        localStorage.removeItem(LEGACY_SELECTED_SUBSCRIPTION_STORAGE_KEY);
        localStorage.removeItem(SUBSCRIPTION_ACCOUNT_ID_STORAGE_KEY);
        broadcastSubscriptionSelection('', '');
      } catch (err) {
        console.warn('AppHeader: failed to clear stored subscription', err);
      }
    }

    prevIsAuthenticatedRef.current = isAuthenticated;
  }, [broadcastSubscriptionSelection, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;

    const loadSubscriptions = async () => {
      setSubscriptionsLoading(true);
      setSubscriptionsError('');
      try {
        const token = await getToken();
        const response = await fetch('/api/apim/subscriptions', {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(text || `Request failed with status ${response.status}`);
        }

        const body = await response.json();
        if (cancelled) return;

        const list = Array.isArray(body?.value) ? body.value : [];
        setSubscriptions(list);

        let storedId = null;
        try {
          storedId = localStorage.getItem(SELECTED_SUBSCRIPTION_STORAGE_KEY);
        } catch (err) {
          console.warn('AppHeader: unable to read stored subscription', err);
        }

        if (!storedId) {
          try {
            const legacy = localStorage.getItem(LEGACY_SELECTED_SUBSCRIPTION_STORAGE_KEY);
            if (legacy) {
              storedId = legacy;
              localStorage.removeItem(LEGACY_SELECTED_SUBSCRIPTION_STORAGE_KEY);
            }
          } catch (err) {
            console.warn('AppHeader: unable to migrate legacy subscription key', err);
          }
        }

        const candidateId = storedId || selectedSubscriptionId;
        const hasStored = candidateId && list.some((item) => item?.name === candidateId);
        const defaultItem = list.find((item) => item?.isDefault === true);
        const nextSubscription = hasStored
          ? list.find((item) => item?.name === candidateId)
          : defaultItem || list[0] || null;

        persistSubscriptionSelection(nextSubscription);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load API Management subscriptions', err);
        setSubscriptions([]);
        persistSubscriptionSelection(null);
        setSubscriptionsError(err?.message || 'Unable to load subscriptions');
      } finally {
        if (!cancelled) {
          setSubscriptionsLoading(false);
        }
      }
    };

    loadSubscriptions();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getToken, persistSubscriptionSelection, selectedSubscriptionId]);

  // fonction pour se sign si connu du site internet
  const handleLoginRedirect = async () => {
    try {
      const res = await fetch("/api/config");
      const config = await res.json();

      /* ------------------------------------------------------------------ *
       * 1. Build the normal login request (your helper keeps scopes etc.).
       * 2. Spread it into the RedirectRequest and add  redirectStartPage.
       *    MSAL stores this value in sessionStorage and, after the token
       *    dance, automatically navigates back to it.                    *
       * ------------------------------------------------------------------ */

      const request = loginRequest({
        SP_API_ENDPOINT: config.SP_API_ENDPOINT
      });

      await instance.loginRedirect({
        ...request,
        redirectStartPage: window.location.href   // ⭐ <-- key line
      });
    } catch (error) {
      console.error("Erreur de connexion :", error);
    }
  };

  // fonction pour se déconnecter une fois authentifié
  const handleLogoutRedirect = () => {
    instance.logoutRedirect().catch((error) => console.log(error));
  };

  useEffect(() => {
    setResourcesMenuOpen(false);
    setDashboardMenuOpen(false);
  }, [location.pathname]);

  const resourceLinks = useMemo(
    () => [
      { id: 'getting-started', label: 'Getting started', path: '/getting-started' },
      { id: 'self-service', label: 'Self service', path: '/self-service' },
      { id: 'bmn-integration', label: 'BMN integration', path: '/bmn-integration' }
    ],
    [],
  );

  const dashboardLinks = useMemo(
    () => [
      { id: 'incident', label: 'Incident', path: '/power-bi' },
      { id: 'finops', label: 'FinOps', path: '/finops' },
      { id: 'greenops', label: 'GreenOps', path: '/greenops' },
      { id: 'tdp-asset', label: 'TDP Asset', path: '/tdp-asset' },
    ],
    [],
  );

  const handleNavigateTo = useCallback(
    (path) => {
      if (!path) {
        return;
      }
      setResourcesMenuOpen(false);
      setDashboardMenuOpen(false);
      if (path.startsWith('https')) {
        window.open(path, '_blank');
        } else if (location.pathname !== path) {
        navigate(path);
      }
    },
    [location.pathname, navigate],
  );

  const isActivePath = useCallback(
    (paths) => paths.some((target) => location.pathname.startsWith(target)),
    [location.pathname],
  );

  const coreServicesActive = isActivePath(['/core-services']);
  const resourcesActive = isActivePath(['/getting-started', '/faq', '/self-service', '/bmn-integration']);
  const dashboardActive = isActivePath(['/power-bi', '/finops', '/greenops', '/tdp-asset']);
  const adminActive = isActivePath(['/admin/self-service']);

  return (
    <>
      <Header classes="app-header">
        
        <HeaderBrand classes="header-logo ml-s mr-s">
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img
              src={logoSrc}
              alt="thales logo"
              width={117}
              height={15}
            />
            {isDesktop && (
              <Typography classes="text-bluegrey-500 dark:text-bluegrey-300 pl-l">
                TrustNest Center
              </Typography>
            )}
          </Link>
        </HeaderBrand>
        <HeaderDivider />

        <HeaderMenu classes="app-header-menu">
          {isDesktop  && isAuthenticated &&  (
            <HeaderMenuLeft classes="app-header-menu-left">
              <nav className="app-header-nav" aria-label="Primary navigation">
                <Button
                  variant="ghost"
                  color="neutral"
                  classes={`app-header-nav-button${coreServicesActive ? ' app-header-nav-button-active' : ''}`}
                  onClickEvent={() => handleNavigateTo('/core-services')}
                >
                  Core services
                </Button>
                <Dropdown
                  visible={resourcesMenuOpen}
                  onVisibleChange={setResourcesMenuOpen}
                >
                  <DropdownTrigger>
                    <Button
                      variant="ghost"
                      color="neutral"
                      classes={`app-header-nav-button app-header-nav-button-with-icon${
                        resourcesActive ? ' app-header-nav-button-active' : ''
                      }`}
                    >
                      Resources
                      <Icon icon="expand_more" size="small" classes="app-header-nav-icon" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownOverlay classes="app-header-dropdown-overlay">
                    <MenuItemList>
                      {resourceLinks.map((item) => (
                        <MenuItem key={item.id} id={`nav-resource-${item.id}`} onClick={() => handleNavigateTo(item.path)}>
                          <MenuItemLabel>{item.label}</MenuItemLabel>
                        </MenuItem>
                      ))}
                    </MenuItemList>

                  </DropdownOverlay>
                </Dropdown>

                <Dropdown
                  visible={dashboardMenuOpen}
                  onVisibleChange={setDashboardMenuOpen}
                >
                  <DropdownTrigger>
                    <Button
                      variant="ghost"
                      color="neutral"
                      classes={`app-header-nav-button app-header-nav-button-with-icon${
                        dashboardActive ? ' app-header-nav-button-active' : ''
                      }`}
                    >
                      Dashboard
                      <Icon icon="expand_more" size="small" classes="app-header-nav-icon" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownOverlay classes="app-header-dropdown-overlay">
                    <MenuItemList>
                      {dashboardLinks.map((item) => (
                        <MenuItem key={item.id} id={`nav-dashboard-${item.id}`} onClick={() => handleNavigateTo(item.path)}>
                          <MenuItemLabel>{item.label}</MenuItemLabel>
                        </MenuItem>
                      ))}
                    </MenuItemList>
                  </DropdownOverlay>
                </Dropdown>
                <Button
                  variant="ghost"
                  color="neutral"
                  classes="app-header-nav-button"
                  onClickEvent={() => {
                    window.open('https://postit.service-now.com/esc?id=tdf_ec_home', '_blank', 'noopener,noreferrer');
                  }}
                >
                  Support
                  <Icon icon="open_in_new" size="small" classes="app-header-nav-icon" />
                </Button>
                
                {isAuthenticated && rolesLoaded && hasAdminRole(roles) && (
                  <Button
                    variant="ghost"
                    color="neutral"
                    classes={`app-header-nav-button${adminActive ? ' app-header-nav-button-active' : ''}`}
                    onClickEvent={() => handleNavigateTo('/admin/self-service')}
                  >
                    Manage Self service
                  </Button>
                )}
              </nav>
            </HeaderMenuLeft>
          )}
          <HeaderMenuRight classes="app-header-menu-right">
            
            {isAuthenticated && (
              <>
                <Button
                  variant="ghost"
                  onClickEvent={() => setSettingsOpen(true)}
                >
                  <Icon icon="settings" />
                </Button>
              </>
            )}

            <ThemeToggle />
            <Dropdown>
              {isAuthenticated ? (
                <>
                  <DropdownTrigger>
                    <Button
                      variant="ghost"
                      classes="app-header-user-button"
                      title={activeAccount?.name || 'Account menu'}
                    >
                      <span className="app-header-user-label">{activeAccount?.name || 'Account'}</span>
                      <Icon icon="account_circle" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownOverlay classes="app-header-dropdown-overlay">
                    <MenuItemList>
                      <MenuItem id="menu-item-1" onClick={handleLogoutRedirect}>
                        <MenuItemLabel>Sign out <Icon icon="logout" classes="px-m" size="small" /></MenuItemLabel>
                      </MenuItem>
                      <MenuItem
                        id="menu-item-2"
                        onClick={() => { window.location.href = 'https://aka.ms/mfasetup'; }}
                      >
                        <MenuItemLabel>Change password <Icon icon="forward" classes="px-m" size="small" /></MenuItemLabel>
                      </MenuItem>
                      <MenuItem
                        id="menu-item-3"
                        onClick={() => window.open(
                          'https://postit.service-now.com/esc?id=sc_cat_item&sys_id=afdbb62947d99e10b694ba5e036d43c8',
                          '_blank'
                        )}
                      >
                        <MenuItemLabel>Submit an idea <Icon icon="emoji_objects" classes="px-m" size="small" /></MenuItemLabel>
                      </MenuItem>
                    </MenuItemList>
                  </DropdownOverlay>
                </>
              ) : (
                <DropdownTrigger>
                  <Button onClickEvent={handleLoginRedirect}><span>Log In</span><Icon icon="account_circle" /></Button>
                </DropdownTrigger>
              )}
            </Dropdown>
          </HeaderMenuRight>
        </HeaderMenu>
      </Header>      
      {isAuthenticated && (
        <Modal open={settingsOpen} onCloseModal={() => setSettingsOpen(false)}>
          <ModalHeader>
            <ModalTitle>
              <Icon icon="account_circle" classes={'mt-xs'} />
              <div>
                <Typography variant="heading-6">Account Settings</Typography>
                <ModalSubtitle>Your profile details</ModalSubtitle>
              </div>
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <Typography classes={darkMode ? 'text-blue-50' : ''} variant="body-1">Name: {activeAccount?.name ?? '—'}</Typography>
            <Typography classes={darkMode ? 'text-blue-50' : ''}  variant="body-1">Username: {activeAccount?.username ?? '—'}</Typography>
            <Typography classes={darkMode ? 'text-blue-50' : ''}  variant="body-1">Home Account ID: {activeAccount?.homeAccountId ?? '—'}</Typography>
            <Typography classes={darkMode ? 'text-blue-50' : ''} variant="body-1">Roles: <RolesDisplay /></Typography>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Typography variant="body-1" classes={darkMode ? 'text-blue-50' : ''}>
                API Management subscriptions
              </Typography>
              {subscriptionsLoading && (
                <Typography classes={darkMode ? 'text-bluegrey-100' : 'text-bluegrey-700'}>Loading subscriptions…</Typography>
              )}
              {!subscriptionsLoading && subscriptionsError && (
                <Typography style={{ color: '#ef4444' }}>
                  Failed to load subscriptions: {subscriptionsError}
                </Typography>
              )}
              {!subscriptionsLoading && !subscriptionsError && subscriptions.length === 0 && (
                <Typography classes={darkMode ? 'text-bluegrey-100' : 'text-bluegrey-700'}>
                  No subscriptions available for your account.
                </Typography>
              )}
              {!subscriptionsLoading && !subscriptionsError && subscriptions.length > 0 && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {subscriptions.map((subscription) => {
                      const displayName = subscription?.properties?.displayName || subscription.name;
                      const productId = subscription?.productId || subscription?.properties?.productId || '—';
                      const isSelected = subscription?.name === selectedSubscriptionId;
                      return (
                        <label
                          key={subscription?.name || displayName}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '10px 12px',
                            borderRadius: 8,
                            border: isSelected ? '1px solid rgba(59,130,246,0.6)' : '1px solid rgba(148,163,184,0.3)',
                            background: isSelected ? (darkMode ? 'rgba(30,64,175,0.2)' : 'rgba(191,219,254,0.35)') : 'transparent',
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            type="radio"
                            name="subscription-choice"
                            checked={isSelected}
                            onChange={() => handleSelectSubscription(subscription?.name)}
                            style={{ accentColor: '#2563eb' }}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontWeight: 600 }}>{displayName}</span>
                            <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>Product: {productId}</span>
                            {subscription?.isDefault && (
                              <span style={{ fontSize: '0.75rem', color: '#2563eb', fontWeight: 600 }}>Default subscription</span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

          </ModalBody>
          <ModalDivider />
          <ModalFooter classes="justify-end">
            <Button
              variant="outline"
              color="primary"
              onClickEvent={() => setSettingsOpen(false)}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}

    </>
  );
};

export default AppHeader;
